import { Box } from '@chakra-ui/react'
import type { ReactNode } from 'react'

import { useChartColors } from '../../components/charts/common/useChartColors'
import { useIsDark } from '../../hooks/useIsDark'
import { ChartLegendConstants } from './ChartLegend/ChartLegend.constants'
import { ChartLineConstants } from './ChartLine/ChartLine.constants'

export interface LineSeriesMarkerProps {
  color: string
  fillColor: string
  pointRadius?: number
  lineWidth?: number
  /** Stable id so multi-row SVGs don't collide on mask urls. */
  maskId?: string
}

/**
 * Line + center node matching ChartLine.Points on light dashboards (Figma):
 * solid series-color fill, not tinted `fillColor`.
 * Line is masked at the outer stroke edge.
 */
export const LineSeriesMarker = ({
  color,
  fillColor: _fillColor,
  pointRadius = ChartLineConstants.defaultPointRadius,
  lineWidth = ChartLineConstants.defaultLineWidth,
  maskId = 'chart-line-series-point-gap',
}: LineSeriesMarkerProps): ReactNode => {
  const chartColors = useChartColors()
  const isDark = useIsDark()
  const { markerViewBoxW: w, markerViewBoxH: h } = ChartLegendConstants
  const cx = w / 2
  const cy = h / 2
  const strokeWidth = ChartLineConstants.pointStrokeWidth
  const holeRadius = pointRadius + strokeWidth / 2
  const dotStroke = isDark ? chartColors.lineDotStroke : color

  return (
    <Box
      as="span"
      display="inline-flex"
      role="img"
      aria-hidden
      flexShrink={0}
      alignItems="center"
    >
      <svg
        width={w}
        height={h}
        viewBox={`0 0 ${w} ${h}`}
        style={{ flexShrink: 0, display: 'block' }}
      >
        <defs>
          <mask id={maskId} maskUnits="userSpaceOnUse">
            <rect width={w} height={h} fill="white" />
            <circle cx={cx} cy={cy} r={holeRadius} fill="black" />
          </mask>
        </defs>
        <line
          x1={0}
          y1={cy}
          x2={w}
          y2={cy}
          stroke={color}
          strokeWidth={lineWidth}
          strokeLinecap="round"
          mask={`url(#${maskId})`}
        />
        <circle
          cx={cx}
          cy={cy}
          r={pointRadius}
          fill={color}
          stroke={dotStroke}
          strokeWidth={strokeWidth}
        />
      </svg>
    </Box>
  )
}
